/**
 * Brief first-encounter teaching pause (LOCKED-DESIGN 12.2 / 18.10). Advanced
 * systems (the first armed red orb, the first receiver jam) get a short,
 * dismissable modal the FIRST time they are met in unranked play — the Phase-3
 * fly-by toasts were missed, so these now momentarily freeze the sim while the
 * beat teaches the verb (the Engine owns the freeze; this owns the DOM card).
 *
 * Ranked runs never pause for teaching (Section 12.2). The Engine only opens
 * this outside the prologue and persists a "seen" flag so it fires once.
 *
 * Skippable with a click, Space/Enter, or Escape. A capture-phase key handler
 * consumes the dismiss keys so Escape resolves the beat instead of leaking to
 * the shell pause menu. Respects prefers-reduced-motion (no pop animation).
 */

export interface FirstEncounterSpec {
  /** small deadpan-facility eyebrow, e.g. "FIRST CONTACT · RECEIVER JAM" */
  eyebrow: string;
  title: string;
  body: string;
  /** css hex accent (signal color for the system being taught) */
  accent: string;
  /**
   * Optional dismiss-button label (LOCKED-DESIGN 18.11 Simple voice). When set,
   * it overrides the default "Got it, continue ▸" copy — Simple passes the
   * early-reader "GOT IT ▸" / "Ⓐ GOT IT ▸" line. The pad prefix is the caller's
   * concern when a custom label is supplied.
   */
  dismiss?: string;
}

export class FirstEncounter {
  active = true;
  private readonly root: HTMLDivElement;
  private dismissCb: (() => void) | null = null;
  private readonly onKey: (event: KeyboardEvent) => void;

  constructor(container: HTMLElement, spec: FirstEncounterSpec, padMode: boolean) {
    const reduced =
      typeof matchMedia !== "undefined" && matchMedia("(prefers-reduced-motion: reduce)").matches;

    const root = document.createElement("div");
    root.dataset.testid = "first-encounter";
    root.style.cssText =
      "position:absolute;inset:0;z-index:7;display:flex;align-items:center;justify-content:center;" +
      "pointer-events:auto;cursor:pointer;background:rgba(4,6,11,0.62);backdrop-filter:blur(2px);" +
      "font:14px/1.5 ui-monospace,monospace;";

    const card = document.createElement("div");
    card.style.cssText =
      `max-width:min(460px,86vw);padding:20px 24px;text-align:left;border-radius:12px;` +
      `background:rgba(9,12,18,0.96);border:1px solid ${spec.accent};` +
      `box-shadow:0 18px 60px rgba(0,0,0,0.55),0 0 26px ${spec.accent}44;transform-origin:center;`;

    const eyebrow = document.createElement("div");
    eyebrow.style.cssText =
      `font-size:11px;font-weight:800;letter-spacing:0.16em;text-transform:uppercase;color:${spec.accent};`;
    eyebrow.textContent = spec.eyebrow;

    const title = document.createElement("div");
    title.style.cssText =
      "margin-top:6px;font-size:19px;font-weight:800;letter-spacing:0.3px;color:#eef2f8;";
    title.textContent = spec.title;

    const body = document.createElement("div");
    body.style.cssText = "margin-top:10px;font-size:14px;line-height:1.55;color:#c7d0e0;";
    body.textContent = spec.body;

    const dismiss = document.createElement("button");
    dismiss.type = "button";
    dismiss.dataset.testid = "first-encounter-dismiss";
    dismiss.textContent = spec.dismiss ?? (padMode ? "Ⓐ Got it, continue ▸" : "Got it, continue ▸  (Space)");
    // width:auto overrides the shell's global `button { width: 100% }`.
    dismiss.style.cssText =
      `margin-top:16px;width:auto;pointer-events:auto;cursor:pointer;font:13px ui-monospace,monospace;` +
      `font-weight:800;color:#070a10;background:${spec.accent};border:none;border-radius:7px;padding:8px 16px;`;

    card.append(eyebrow, title, body, dismiss);
    root.append(card);
    // Clicking anywhere (card, button, or backdrop) resolves the beat.
    root.addEventListener("click", () => this.dismiss());
    container.appendChild(root);
    this.root = root;

    if (!reduced) {
      card.style.transition = "none";
      card.style.transform = "scale(0.94)";
      card.style.opacity = "0";
      requestAnimationFrame(() => {
        card.style.transition = "transform .18s cubic-bezier(.2,.8,.3,1), opacity .18s ease-out";
        card.style.transform = "scale(1)";
        card.style.opacity = "1";
      });
    }

    this.onKey = (event: KeyboardEvent): void => {
      if (!this.active) return;
      if (
        event.code === "Space" || event.code === "Enter" ||
        event.code === "NumpadEnter" || event.code === "Escape"
      ) {
        event.preventDefault();
        event.stopImmediatePropagation();
        this.dismiss();
      }
    };
    // Capture phase so the dismiss keys resolve here BEFORE the shell's window
    // handler opens the pause menu or the input sampler records a stray edge.
    window.addEventListener("keydown", this.onKey, true);
  }

  onDismiss(cb: () => void): void {
    this.dismissCb = cb;
  }

  private dismiss(): void {
    if (!this.active) return;
    this.active = false;
    window.removeEventListener("keydown", this.onKey, true);
    this.root.remove();
    this.dismissCb?.();
  }

  /** Tear down without firing the resume callback (run reset / engine destroy). */
  destroy(): void {
    this.active = false;
    window.removeEventListener("keydown", this.onKey, true);
    this.root.remove();
  }
}
